import React, { useState, useEffect } from 'react';
import { TrustedAdvisorService } from '../../lib/trustedAdvisorService';
import { AdvisorAuditLog, AuditAction, DocumentType, AuditLogFilters } from '../../types/trustedAdvisor';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Select } from '../common/Select';
import { Input } from '../common/Input';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Badge } from '../common/Badge';
import { 
  Activity, 
  Filter, 
  Search, 
  Calendar,
  User,
  FileText,
  Shield,
  Eye,
  Edit,
  Lock,
  Unlock,
  Users,
  Mail,
  Clock,
  RefreshCw,
  Download
} from 'lucide-react';

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AdvisorAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const actionOptions = [
    { value: '', label: 'All Actions' },
    { value: 'advisor_added', label: 'Advisor Added' },
    { value: 'advisor_removed', label: 'Advisor Removed' },
    { value: 'advisor_activated', label: 'Advisor Activated' },
    { value: 'permission_granted', label: 'Permission Granted' },
    { value: 'permission_revoked', label: 'Permission Revoked' },
    { value: 'permission_updated', label: 'Permission Updated' },
    { value: 'document_accessed', label: 'Document Accessed' },
    { value: 'document_viewed', label: 'Document Viewed' },
    { value: 'document_edited', label: 'Document Edited' },
    { value: 'unlock_requested', label: 'Unlock Requested' },
    { value: 'unlock_approved', label: 'Unlock Approved' },
    { value: 'unlock_blocked', label: 'Unlock Blocked' },
    { value: 'unlock_completed', label: 'Unlock Completed' },
    { value: 'invitation_sent', label: 'Invitation Sent' },
    { value: 'invitation_accepted', label: 'Invitation Accepted' },
    { value: 'invitation_declined', label: 'Invitation Declined' }
  ];

  const documentTypeOptions = [
    { value: '', label: 'All Documents' },
    { value: 'will', label: 'Will' },
    { value: 'trust', label: 'Trust' },
    { value: 'power_of_attorney', label: 'Power of Attorney' },
    { value: 'health_directive', label: 'Health Directive' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'financial_account', label: 'Financial Account' },
    { value: 'investment', label: 'Investment' },
    { value: 'retirement', label: 'Retirement' },
    { value: 'medical_record', label: 'Medical Record' },
    { value: 'appointment', label: 'Appointment' },
    { value: 'medication', label: 'Medication' },
    { value: 'pet_profile', label: 'Pet Profile' },
    { value: 'legacy_item', label: 'Legacy Item' },
    { value: 'digital_asset', label: 'Digital Asset' }
  ];

  useEffect(() => {
    loadAuditLogs();
  }, [filters]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const auditFilters: AuditLogFilters = { ...filters };
      if (dateFrom) auditFilters.date_from = dateFrom;
      if (dateTo) auditFilters.date_to = dateTo;
      
      const data = await TrustedAdvisorService.getAuditLogs({ ...auditFilters, limit: 100 });
      setLogs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof AuditLogFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  const getActionIcon = (action: AuditAction) => {
    switch (action) {
      case 'advisor_added':
      case 'advisor_activated':
        return <Users className="w-4 h-4 text-green-600" />;
      case 'advisor_removed':
        return <Users className="w-4 h-4 text-red-600" />;
      case 'permission_granted':
      case 'permission_updated':
        return <Shield className="w-4 h-4 text-blue-600" />;
      case 'permission_revoked':
        return <Shield className="w-4 h-4 text-red-600" />;
      case 'document_accessed':
      case 'document_viewed':
        return <Eye className="w-4 h-4 text-blue-600" />;
      case 'document_edited':
        return <Edit className="w-4 h-4 text-orange-600" />;
      case 'unlock_requested':
        return <Unlock className="w-4 h-4 text-yellow-600" />;
      case 'unlock_approved':
        return <Unlock className="w-4 h-4 text-green-600" />;
      case 'unlock_blocked':
        return <Lock className="w-4 h-4 text-red-600" />;
      case 'unlock_completed':
        return <Unlock className="w-4 h-4 text-purple-600" />;
      case 'invitation_sent':
        return <Mail className="w-4 h-4 text-blue-600" />;
      case 'invitation_accepted':
        return <Mail className="w-4 h-4 text-green-600" />;
      case 'invitation_declined':
        return <Mail className="w-4 h-4 text-red-600" />;
      default:
        return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActionColor = (action: AuditAction) => {
    switch (action) {
      case 'advisor_added':
      case 'advisor_activated':
      case 'permission_granted':
      case 'unlock_approved':
      case 'invitation_accepted':
        return 'bg-green-100 text-green-800';
      case 'advisor_removed':
      case 'permission_revoked':
      case 'unlock_blocked':
      case 'invitation_declined':
        return 'bg-red-100 text-red-800';
      case 'document_accessed':
      case 'document_viewed':
      case 'unlock_requested':
      case 'invitation_sent':
        return 'bg-blue-100 text-blue-800';
      case 'document_edited':
      case 'permission_updated':
        return 'bg-orange-100 text-orange-800';
      case 'unlock_completed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatActionLabel = (action: AuditAction) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredLogs = logs.filter(log => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const actionText = formatActionLabel(log.action).toLowerCase();
      const detailsText = JSON.stringify(log.action_details).toLowerCase();
      return actionText.includes(searchLower) || detailsText.includes(searchLower);
    }
    return true;
  });

  const exportAuditLogs = () => {
    const csvContent = [
      ['Date', 'Action', 'Details', 'IP Address', 'User Agent'].join(','),
      ...filteredLogs.map(log => [
        formatDate(log.created_at),
        formatActionLabel(log.action),
        JSON.stringify(log.action_details),
        log.ip_address || '',
        log.user_agent || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Activity Log</h2>
          <p className="text-gray-600 mt-1">
            Track all trusted advisor activities and system events
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={exportAuditLogs}
            variant="outline"
            className="flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button
            onClick={loadAuditLogs}
            variant="outline"
            className="flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            className="flex items-center"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Hide' : 'Show'} Filters
          </Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search actions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Action Type
              </label>
              <Select
                value={filters.action || ''}
                onChange={(value) => handleFilterChange('action', value)}
                options={actionOptions}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Type
              </label>
              <Select
                value={filters.document_type || ''}
                onChange={(value) => handleFilterChange('document_type', value)}
                options={documentTypeOptions}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date From
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date To
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>

            <div className="flex items-end">
              <Button
                onClick={clearFilters}
                variant="outline"
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Error Display */}
      {error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center">
            <Activity className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </Card>
      )}

      {/* Audit Logs */}
      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP Address
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
        <Activity className="w-12 h-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No activity found
                      </h3>
                      <p className="text-gray-600">
                        {searchTerm || Object.keys(filters).some(k => filters[k as keyof AuditLogFilters])
                          ? 'Try adjusting your filters or search terms.'
                          : 'Activity will appear here as trusted advisors interact with your documents.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getActionIcon(log.action)}
                        <div className="ml-3">
                          <Badge className={getActionColor(log.action)}>
                            {formatActionLabel(log.action)}
                          </Badge>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {log.action_details && Object.keys(log.action_details).length > 0 ? (
                          <div className="space-y-1">
                            {Object.entries(log.action_details).map(([key, value]) => (
                              <div key={key} className="flex">
                                <span className="font-medium text-gray-700 mr-2">
                                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                                </span>
                                <span className="text-gray-600">
                                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500">No additional details</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {formatDate(log.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.ip_address || 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Summary */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Activity className="w-5 h-5 text-blue-500 mr-2" />
            <span className="text-blue-700 font-medium">
              Showing {filteredLogs.length} of {logs.length} total activities
            </span>
          </div>
          <div className="text-sm text-blue-600">
            Last updated: {new Date().toLocaleString()}
          </div>
      </div>
    </Card>
    </div>
  );
};

export default AuditLogs; 